import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { PagePublisherImage } from 'common/interfaces/agg-page';
import { DraggableImageCard } from 'common/ui/draggable-image-card';
import { ChangeEvent, FC, useRef, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import type { IPageFormV2 } from '../../../page-builder-v2.types';
import { PublisherArchive } from './publisher-archive';
import { PublisherImageCreator } from './publisher-image-creator';
import { styles } from './publisher-images-section.styles';

interface IVisiblePubImage extends PagePublisherImage {
  originalIndex: number;
}

export const PublisherImagesSection: FC = () => {
  const { control } = useFormContext<IPageFormV2>();
  const [showArchive, setShowArchive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleAddNewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleAddLocalImage(file, URL.createObjectURL(file));
    }
    e.target.value = '';
  };

  const { append, fields, remove, update } = useFieldArray({
    control,
    name: 'publisherImages',
  });

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const visibleImages: IVisiblePubImage[] = fields
    .map((field, index) => ({ ...field, originalIndex: index }))
    .filter((f) => f.type !== 'deleted');

  const selectedUrls = visibleImages.map((img) => img.url || '').filter(Boolean);

  const handleAddLocalImage = (file: File, previewSrc: string) => {
    const newImage: PagePublisherImage = {
      assets_type: 'publisherImages',
      type: 'new',
      file,
      preview: previewSrc,
      file_data: {},
    };
    append(newImage);
  };

  const handleDelete = (originalIndex: number) => {
    const item = fields[originalIndex];
    if (item.type === 'new' || item.type === 'renew') {
      remove(originalIndex);
    } else {
      update(originalIndex, { ...item, type: 'deleted' });
    }
  };

  const handleArchiveImageClick = (item: PagePublisherImage) => {
    const existingIdx = fields.findIndex(
      (elem) => elem.url === item.url && elem.type !== 'deleted',
    );
    if (existingIdx !== -1) {
      handleDelete(existingIdx);
    } else {
      append(item);
    }
  };

  const handleSwap = (visIdx1: number, visIdx2: number) => {
    const origIdx1 = visibleImages[visIdx1].originalIndex;
    const origIdx2 = visibleImages[visIdx2].originalIndex;
    const item1 = fields[origIdx1];
    const item2 = fields[origIdx2];
    update(origIdx1, { ...item2, type: item2.type === 'new' ? 'new' : 'renew' });
    update(origIdx2, { ...item1, type: item1.type === 'new' ? 'new' : 'renew' });
  };

  const handleDrop = (dropIndex: number) => {
    if (draggedIndex !== null && draggedIndex !== dropIndex) {
      handleSwap(draggedIndex, dropIndex);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  return (
    <Accordion
      defaultExpanded
      disableGutters
      sx={{
        '&:before': { display: 'none' },
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px !important',
        boxShadow: 'none',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          '& .MuiAccordionSummary-content': { margin: '12px 0' },
          '& .MuiAccordionSummary-content.Mui-expanded': { margin: '12px 0' },
          '&.Mui-expanded': { minHeight: 52 },
          minHeight: 52,
          px: 2.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mr: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Typography fontWeight={600} variant="subtitle1">Publisher Images</Typography>
              {visibleImages.length > 0 && (
                <Chip color="primary" label={visibleImages.length} size="small" variant="outlined" />
              )}
            </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {visibleImages.length > 0 && (
              <Button
                color="primary"
                onClick={handleAddNewClick}
                size="small"
                startIcon={<AddIcon />}
                variant="outlined"
              >
                Add New
              </Button>
            )}
            <Button
              color="primary"
              onClick={(e) => { e.stopPropagation(); setShowArchive(!showArchive); }}
              size="small"
              startIcon={<PhotoLibraryIcon />}
              variant="outlined"
            >
              Add from Uploaded
            </Button>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ borderTop: '1px solid', borderColor: 'divider', p: visibleImages.length === 0 ? 0 : 2 }}>
      <input
        ref={fileInputRef}
        accept="image/jpeg, image/png, image/jpg, image/svg"
        hidden
        onChange={handleFileChange}
        type="file"
      />

      <Dialog
        fullWidth
        maxWidth="md"
        onClose={() => setShowArchive(false)}
        open={showArchive}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Image Archive
          <IconButton onClick={() => setShowArchive(false)} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <PublisherArchive onImageClick={handleArchiveImageClick} selectedUrls={selectedUrls} />
        </DialogContent>
      </Dialog>

      {visibleImages.length === 0 ? (
        <Box
          sx={{
            '& > *': {
              border: 'none !important',
              borderRadius: '0 0 8px 8px !important',
              height: '220px !important',
            },
          }}
        >
          <PublisherImageCreator onAdd={handleAddLocalImage} />
        </Box>
      ) : (
        <Box sx={styles.listContainer}>
          {visibleImages.map((image, index) => (
            <DraggableImageCard
              key={image.id || image.preview || index}
              dragOverIndex={dragOverIndex}
              height={160}
              index={index}
              onDelete={() => handleDelete(image.originalIndex)}
              onDragLeave={() => setDragOverIndex(null)}
              onDragOver={setDragOverIndex}
              onDragStart={setDraggedIndex}
              onDrop={handleDrop}
              onSwap={handleSwap}
              src={image.url || image.preview || ''}
              totalItems={visibleImages.length}
              width={160}
            />
          ))}
        </Box>
      )}
      </AccordionDetails>
    </Accordion>
  );
};
